import { createAuthClient } from "better-auth/react";
import { anonymousClient } from "better-auth/client/plugins";
import { passkeyClient } from "@better-auth/passkey/client";
import { appConfig } from "./config";

/**
 * Better Auth browser client: silent anonymous sessions (Rung 0) + passkey
 * upgrade (Rung 1). Federate needs no client plugin — see federateSignInUrl.
 */
export function createAuthApi(baseURL: string = appConfig.apiBase) {
  return createAuthClient({
    baseURL: baseURL || undefined,
    plugins: [anonymousClient(), passkeyClient()],
  });
}

export type AuthApi = ReturnType<typeof createAuthApi>;

/**
 * Start the Amazon Federate (Midway) flow and return the IdP URL to navigate
 * to. Better Auth 1.7+ routes generic-oauth providers through the CORE
 * sign-in/social endpoint (the plugin registers no /sign-in/oauth2 route), and
 * `signIn.social` types `provider` as the built-in social list, so this goes
 * through the client's own fetch — which keeps the configured baseURL and the
 * injected fetch implementation tests rely on.
 */
export async function federateSignInUrl(auth: AuthApi, callbackURL: string): Promise<string> {
  const res = await auth.$fetch<{ url?: string }>("/sign-in/social", {
    method: "POST",
    body: { provider: "federate", callbackURL },
  });
  const url = res.data?.url;
  if (!url) throw new Error("Midway sign-in is unavailable for this environment");
  return assertNavigableUrl(url);
}

/**
 * Refuse to hand anything but plain https to `window.location.href`.
 *
 * `ensureSession` assigns this value to the location, and a `javascript:` or
 * `data:` URL there is script execution inside our own origin. The value comes
 * from our own API over TLS, so this is defence in depth rather than a live
 * hole — but it is the kind of hole that only has to open once, and the check
 * costs four lines. `https:` only: every environment that reaches this path is
 * served over TLS and the IdP is always remote.
 */
function assertNavigableUrl(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error("Midway sign-in returned a malformed URL");
  }
  if (parsed.protocol !== "https:") {
    throw new Error(`Midway sign-in returned a non-https URL (${parsed.protocol})`);
  }
  return parsed.toString();
}

/**
 * "redirecting" means a navigation to the IdP is already in flight: the caller
 * must keep showing the booting state rather than an error, because this tab is
 * about to be replaced.
 */
export type SessionBootstrap = "ready" | "redirecting" | "failed";

const FEDERATE_ATTEMPT_KEY = "ec2.federate.attempt";
const FEDERATE_RETRY_AFTER_MS = 20_000;

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null; // storage disabled (some embedded webviews)
  }
}

/**
 * A Midway sign-in that is completed but REFUSED — the person is not in the
 * profile's allowed groups, the claim is misconfigured — returns here with no
 * session, which would start the redirect again at once: an invisible loop
 * between this app and the IdP at full speed, with no error ever on screen.
 *
 * So the automatic redirect may fire once per 20 seconds; anything faster
 * renders the failure state instead, whose retry button passes `force`. The
 * marker lives in sessionStorage because it has to survive the redirect and
 * must not outlive the tab.
 */
function claimFederateAttempt(force: boolean): boolean {
  const store = sessionStore();
  if (!store) return true;
  const last = Number(store.getItem(FEDERATE_ATTEMPT_KEY) ?? 0);
  if (!force && Number.isFinite(last) && Date.now() - last < FEDERATE_RETRY_AFTER_MS) return false;
  store.setItem(FEDERATE_ATTEMPT_KEY, String(Date.now()));
  return true;
}

/**
 * Bootstrap rule (backend contract): the silent anonymous session is created
 * FIRST — every tRPC call without a session is UNAUTHORIZED.
 *
 * `force` marks a bootstrap the person asked for (the retry button), which may
 * redirect to Midway even inside the loop-breaker window.
 */
export async function ensureSession(
  auth: AuthApi,
  opts: { force?: boolean } = {},
): Promise<SessionBootstrap> {
  const existing = await auth.getSession();
  if (existing.data?.session) {
    sessionStore()?.removeItem(FEDERATE_ATTEMPT_KEY);
    return "ready";
  }

  if (appConfig.requireFederate) {
    // Internal stage: Midway is required before the game is reachable at all,
    // and there is exactly one way in, so redirect instead of rendering a
    // button nobody can avoid.
    if (!claimFederateAttempt(opts.force ?? false)) return "failed";
    window.location.href = await federateSignInUrl(auth, window.location.href);
    return "redirecting";
  }

  const signedIn = await auth.signIn.anonymous();
  return signedIn.data && !signedIn.error ? "ready" : "failed";
}
